
import { Component, inject, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { DIALOG_WIDTH_SM, SNACKBAR_DURATION_ERROR, SNACKBAR_DURATION_SUCCESS } from '../../app.constants';
import { AuthService } from '../../auth/auth.service';
import { ConfirmDialogComponent } from '../../main/confirm-dialog/confirm-dialog.component';
import { ChargingStateTranslatePipe } from '../../main/translate/charging-state-translate.pipe';
import { ChargerOverview } from '../../models';
import { ChargerAddDialogComponent } from '../charger-add-dialog/charger-add-dialog.component';
import { ChargersService } from '../chargers.service';


@Component({
	selector: 'app-my-chargers',
	imports: [MatProgressSpinnerModule, MatListModule, MatIconModule, RouterLink, MatSnackBarModule, MatButtonModule, MatCardModule, MatTooltip, ChargingStateTranslatePipe, MatMenuModule],
	templateUrl: './my-chargers.component.html',
	styleUrl: './my-chargers.component.scss'
})
export class MyChargersComponent implements OnInit {
	chargers = signal<ChargerOverview[]>([]);
	chargersLoaded = signal<boolean>(false);

	private chargersService = inject(ChargersService);
	private snackBar = inject(MatSnackBar);
	private dialog = inject(MatDialog);
	public authService = inject(AuthService);

	ngOnInit() {
		this.retrieveChargers();
	}

	async retrieveChargers() {
		this.chargersLoaded.set(false);
		this.chargersService.getChargersOverview().subscribe((chargers) => {
			console.log('retrieveChargers():', chargers);
			// sort
			chargers.sort((a, b) => {
				return a.serialNumber.localeCompare(b.serialNumber);
			});
			this.chargers.set(chargers);
			this.chargersLoaded.set(true);
		});
	}

	async addCharger(serialNumber: string, bluetoothPasskey: string) {
		console.log('addCharger():', serialNumber, bluetoothPasskey);
		try {
			const result = await firstValueFrom(
				this.chargersService.putChargerAdd(serialNumber, bluetoothPasskey)
			);
			if (result.success) {
				console.log('addCharger(): successfully');
				this.snackBar.open($localize`:@@AddSuccessfully:Add successfully`, $localize`:@@Close:Close`, {
					duration: SNACKBAR_DURATION_SUCCESS,
					panelClass: ['snackbar-success']
				});
				await this.retrieveChargers();
			} else {
				console.error('Add failed');
				this.snackBar.open($localize`:@@FailedWithMessage:Failed: ${result.errorMessage}`, $localize`:@@Close:Close`, {
					duration: SNACKBAR_DURATION_ERROR,
					panelClass: ['snackbar-error']
				});
			}
		} catch (err: any) {
			console.error('Add failed');
			this.snackBar.open($localize`:@@ErrorWithMessage:Error: ${err.message}`, $localize`:@@Close:Close`, {
				duration: SNACKBAR_DURATION_ERROR,
				panelClass: ['snackbar-error']
			});
		}
	}

	async deleteCharger(serialNumber: string) {
		console.log('deleteCharger():', serialNumber);
		try {
			const result = await firstValueFrom(
				this.chargersService.deleteCharger(serialNumber)
			);
			if (result.success) {
				console.log('deleteCharger(): successfully');
				this.snackBar.open($localize`:@@DeleteSuccessfully:Delete successfully`, $localize`:@@Close:Close`, {
					duration: SNACKBAR_DURATION_SUCCESS,
					panelClass: ['snackbar-success']
				});
				await this.retrieveChargers();
			} else {
				console.error('Delete failed');
				this.snackBar.open($localize`:@@FailedWithMessage:Failed: ${result.errorMessage}`, $localize`:@@Close:Close`, {
					duration: SNACKBAR_DURATION_ERROR,
					panelClass: ['snackbar-error']
				});
			}
		} catch (err: any) {
			console.error('Delete failed');
			this.snackBar.open($localize`:@@ErrorWithMessage:Error: ${err.message}`, $localize`:@@Close:Close`, {
				duration: SNACKBAR_DURATION_ERROR,
				panelClass: ['snackbar-error']
			});
		}
	}

	async openAddChargerDialog() {
		const ref = this.dialog.open(ChargerAddDialogComponent, {
			width: DIALOG_WIDTH_SM,
			disableClose: true,
			autoFocus: 'first-tabbable',
		});

		const result = await firstValueFrom(ref.afterClosed());
		if (!result) return;
		this.addCharger(result.serialNumber, result.bluetoothPasskey);
	}

	async openDeleteChargerDialog(event: MouseEvent, serial: string) {
		// stop click event for routerLink
		event.preventDefault();
		event.stopPropagation();

		const ref = this.dialog.open(ConfirmDialogComponent, {
			data: {
				title: $localize`:@@DeleteChargerTitle:Delete charger?`,
				message: $localize`:@@DeleteChargerMessage:Are you sure you want to delete charger ${serial}?`,
				confirmText: $localize`:@@Delete:Delete`,
				cancelText: $localize`:@@Cancel:Cancel`,
			},
		});

		const confirmed = await firstValueFrom(ref.afterClosed());
		if (confirmed !== true) return;
		this.deleteCharger(serial);
	}

}
